import { findNextBoundary, timestampToDate } from './dates';
import moment from 'moment';

export const isArrayLength = arr => {
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const getAllBookingHoursArr = (values, timeZone) => {
  const { bookingStartTime, bookingEndTime, bookingLengthType, numberOfBookings } = values;

  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

  if (!startDate || !endDate || !bookingLengthType || !numberOfBookings) {
    return [];
  }

  const createBookingDetails = (startDate, endDate, bookingLengthType, numberOfBookings) => {
    let start = new Date(startDate);
    let end = new Date(endDate);
    let bookingDetails = [];

    if (bookingLengthType === 'weeks') {
      for (let i = 0; i <= numberOfBookings; i++) {
        let bookingStartDate = new Date(start);

        // Adjust the bookingStartDate by adding weeks
        bookingStartDate.setDate(start.getDate() + i * 7);

        // Adjust the bookingEndDate based on the new bookingStartDate
        let bookingEndDate = new Date(bookingStartDate);
        bookingEndDate.setHours(end.getHours());
        bookingEndDate.setMinutes(end.getMinutes());
        bookingEndDate.setSeconds(end.getSeconds());

        const bookingStart = bookingStartDate.getTime();
        const bookingEnd = bookingEndDate.getTime();

        bookingDetails.push({
          startDate: timestampToDate(bookingStart),
          endDate: timestampToDate(bookingEnd),
          displayEndDate: new Date(findNextBoundary(bookingEnd - 1, 'hour', timeZone).getTime()),
          bookingStart: bookingStart,
          bookingEnd: bookingEnd,
        });
      }
    } else if (bookingLengthType === 'months') {
      for (let i = 0; i < numberOfBookings; i++) {
        let currentMonthStart = moment(start)
          .add(i, 'months')
          .toDate();
        let currentMonthEnd = new Date(currentMonthStart);

        currentMonthEnd.setDate(currentMonthStart.getDate() + (end.getDate() - start.getDate()));

        currentMonthStart.setHours(start.getHours());
        currentMonthStart.setMinutes(start.getMinutes());
        currentMonthStart.setSeconds(start.getSeconds());

        currentMonthEnd.setHours(end.getHours());
        currentMonthEnd.setMinutes(end.getMinutes());
        currentMonthEnd.setSeconds(end.getSeconds());

        const bookingStart = currentMonthStart.getTime();
        const bookingEnd = currentMonthEnd.getTime();

        bookingDetails.push({
          startDate: timestampToDate(bookingStart),
          endDate: timestampToDate(bookingEnd),
          displayEndDate: new Date(findNextBoundary(bookingEnd - 1, 'hour', timeZone).getTime()),
          bookingStart: bookingStart,
          bookingEnd: bookingEnd,
        });
      }
    }

    return bookingDetails;
  };

  let bookingDetails = createBookingDetails(
    startDate,
    endDate,
    bookingLengthType,
    numberOfBookings
  );

  return bookingDetails;
};

export const getAllHourSlots = (monthlyTimeSlots, allBookingsArr) => {
  if (monthlyTimeSlots) {
    const allTimeSlots = Object.values(monthlyTimeSlots).flatMap(month => month.timeSlots);
    const availableSlots = [];
    const unavailableSlots = [];

    allBookingsArr.forEach(booking => {
      const bookingStartTime = new Date(booking.startDate).getTime();
      let bookingEndTime = new Date(booking.endDate).getTime();

      // Round the end time up to the next whole hour if it's not on the hour already
      const endDateObj = new Date(bookingEndTime);
      if (endDateObj.getMinutes() === 59 && endDateObj.getSeconds() === 59) {
        endDateObj.setMinutes(0);
        endDateObj.setSeconds(0);
        endDateObj.setMilliseconds(0); // Set milliseconds to 0
        endDateObj.setHours(endDateObj.getHours() + 1);
        bookingEndTime = endDateObj.getTime();
        booking.endDate = endDateObj; // Update the booking object
      } else {
        endDateObj.setMilliseconds(0); // Ensure milliseconds are set to 0 even if not rounding up
        bookingEndTime = endDateObj.getTime();
        booking.endDate = endDateObj; // Update the booking object
      }

      let isAvailable = false;

      for (const slot of allTimeSlots) {
        const slotStartTime = new Date(slot?.attributes?.start).getTime();
        const slotEndTime = new Date(slot?.attributes?.end).getTime();

        // Check if the booking fits entirely within the slot
        if (
          (bookingStartTime >= slotStartTime && bookingEndTime <= slotEndTime) ||
          (bookingStartTime == slotStartTime && bookingEndTime == slotEndTime)
        ) {
          isAvailable = true;
          break;
        }
      }

      if (isAvailable) {
        availableSlots.push(booking);
      } else {
        unavailableSlots.push(booking);
      }
    });

    return { availableSlots, unavailableSlots };
  }

  return { availableSlots: [], unavailableSlots: [] };
};

function degToRad(degrees) {
  return degrees * (Math.PI / 180);
}

function radToDeg(radians) {
  return radians * (180 / Math.PI);
}

export const obfuscateLocation = (center, distanceMeters = 100) => {
  const R = 6371e3; // Earth's radius in meters

  // Random direction (angle in radians)
  const randomAngle = Math.random() * 2 * Math.PI;

  // Calculate the offsets in meters
  const deltaLat = (distanceMeters * Math.cos(randomAngle)) / R;
  const deltaLng = (distanceMeters * Math.sin(randomAngle)) / (R * Math.cos(degToRad(center.lat)));

  // Convert offsets from radians to degrees
  const newLat = center.lat + radToDeg(deltaLat);
  const newLng = center.lng + radToDeg(deltaLng);

  return { lat: newLat, lng: newLng };
};

export function formatBookingDates(selectedDates = []) {
  if (!selectedDates || selectedDates.length === 0) {
    return [];
  }

  // Sort the dates to ensure the start and end dates are correctly assigned
  selectedDates.sort((a, b) => a.valueOf() - b.valueOf());

  if (selectedDates.length === 1) {
    // Handle single date selection by setting endDate to the start of the next day
    const singleDate = selectedDates[0];
    const startDate = singleDate.clone().startOf('day');
    const endDate = startDate
      .clone()
      .add(1, 'day')
      .startOf('day'); // Start of the next day (00:00)

    return [
      {
        bookingStart: startDate.valueOf(),
        bookingEnd: endDate.valueOf(),
        displayEndDate: endDate.clone().format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ'),
        endDate: endDate.toDate(),
        startDate: startDate.toDate(),
      },
    ];
  }

  const result = [];
  let rangeStartIndex = 0;

  for (let i = 1; i <= selectedDates.length; i++) {
    if (
      i === selectedDates.length ||
      !selectedDates[i].isSame(selectedDates[i - 1].clone().add(1, 'day'), 'day')
    ) {
      const rangeStartDate = selectedDates[rangeStartIndex].clone().startOf('day');
      const rangeEndDate = selectedDates[i - 1]
        .clone()
        .add(1, 'day')
        .startOf('day'); // Start of the next day (00:00)

      result.push({
        bookingStart: rangeStartDate.valueOf(),
        bookingEnd: rangeEndDate.valueOf(),
        displayEndDate: rangeEndDate.clone().format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ'),
        endDate: rangeEndDate.toDate(),
        startDate: rangeStartDate.toDate(),
      });

      rangeStartIndex = i;
    }
  }

  return result;
}

export const getFirstBookingDates = (bookings = []) =>
  bookings?.length ? { startDate: bookings[0].startDate, endDate: bookings[0].endDate } : null;

export function formatBookingTimeAndDates(selectedBookingDates = []) {
  return (
    isArrayLength(selectedBookingDates) &&
    selectedBookingDates?.map(booking => {
      const newStartDate = timestampToDate(booking?.bookingStartTime);
      return {
        bookingStart: parseInt(booking.bookingStartTime, 10),
        bookingEnd: parseInt(booking.bookingEndTime, 10),
        startDate: newStartDate,
        endDate: booking.bookingEndDate.date,
      };
    })
  );
}

export function updateBookingSlots(bookingSlots, bookingStartTime, bookingEndTime) {
  // Convert Unix timestamps to Moment.js objects
  const startTimeMoment = bookingStartTime && moment(parseInt(bookingStartTime));
  const endTimeMoment = bookingEndTime && moment(parseInt(bookingEndTime));

  // Extract time parts from these Moment.js objects
  const startTime = startTimeMoment.format('HH:mm:ss');
  const endTime = endTimeMoment.format('HH:mm:ss');

  return bookingSlots.map(slot => {
    // Convert existing dates to Moment.js objects
    const startDateMoment = moment(slot.startDate);
    const endDateMoment = moment(slot.endDate);

    // Extract the date part from the existing dates
    const startDateOnly = startDateMoment.format('YYYY-MM-DD');
    const endDateOnly = endDateMoment.format('YYYY-MM-DD');

    // Combine the date part with the extracted time part
    const updatedStartDate = moment(
      `${startDateOnly} ${startTime}`,
      'YYYY-MM-DD HH:mm:ss'
    ).toDate();
    const updatedEndDate = moment(`${endDateOnly} ${endTime}`, 'YYYY-MM-DD HH:mm:ss').toDate();

    // Update bookingStart and bookingEnd with the new times
    const updatedBookingStart = moment(updatedStartDate).valueOf();
    const updatedBookingEnd = moment(updatedEndDate).valueOf();

    return {
      ...slot,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
      bookingStart: updatedBookingStart,
      bookingEnd: updatedBookingEnd,
    };
  });
}

export function resetTimeSlotToMidnight(selectedTimeSlot = {}) {
  const startDate = selectedTimeSlot?.attributes?.start;
  const endDate = selectedTimeSlot?.attributes?.end;

  // Reset the times to midnight
  const startLimitNew = moment(startDate)
    .startOf('day')
    .toDate();
  const endLimitNew = moment(endDate)
    .add(1, 'day')
    .startOf('day')
    .toDate();

  // Return a new object with the same structure, updating the start and end times
  return {
    id: selectedTimeSlot.id,
    type: selectedTimeSlot.type,
    attributes: {
      ...selectedTimeSlot?.attributes,
      start: startLimitNew,
      end: endLimitNew,
    },
  };
}
export function resetTimeToMidnight(startDate, endDate) {
  return {
    startLimitNew: moment(startDate)
      .startOf('day')
      .toDate(),
    endLimitNew: moment(endDate)
      .add(1, 'day')
      .startOf('day')
      .toDate(),
  };
}

export const getFirstBookingDate = (selectedBookingDates = []) => {
  if (Array.isArray(selectedBookingDates) && selectedBookingDates.length > 0) {
    return selectedBookingDates[0];
  } else {
    console.warn('No booking dates available');
    return null;
  }
};
